"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Calendar, Clock, Search, ChevronLeft, ChevronRight, BookOpen, Sparkles } from "lucide-react"
import Link from "next/link"
import { motion } from "framer-motion"
import { trackPageView, trackButtonClick } from "@/lib/gtm"
import type { BlogPost } from "@/lib/sanity"
import Image from 'next/image'

const calculateReadTime = (content: any) => {
  const textLength = JSON.stringify(content).length
  const wordsPerMinute = 200
  const estimatedWords = textLength / 5 // Rough estimate
  const readTime = Math.ceil(estimatedWords / wordsPerMinute)
  return `${readTime} min read`
}

const POSTS_PER_PAGE = 12

type BlogClientProps = {
  blogPosts: BlogPost[]
}

export default function BlogClient({ blogPosts }: BlogClientProps) {
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState("")

  useEffect(() => {
    trackPageView("Blog Page")
  }, [])

  // Get featured posts (latest 3 only)
  const featuredPosts = blogPosts
    .filter((post) => post.fields.featured)
    .sort((a, b) => new Date(b.fields.publishDate).getTime() - new Date(a.fields.publishDate).getTime())
    .slice(0, 3)

  // Get remaining posts (excluding the latest 3 featured)
  const featuredPostIds = featuredPosts.map((post) => post.sys.id)
  const remainingPosts = blogPosts.filter((post) => !featuredPostIds.includes(post.sys.id))

  // Filter remaining posts based on search
  const filteredPosts = remainingPosts.filter((post) => {
    const matchesSearch =
      searchQuery === "" ||
      post.fields.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.fields.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesSearch
  })

  // Calculate pagination for remaining posts
  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE)
  const startIndex = (currentPage - 1) * POSTS_PER_PAGE
  const endIndex = startIndex + POSTS_PER_PAGE
  const currentPosts = filteredPosts.slice(startIndex, endIndex)

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    trackButtonClick(`Page ${page}`, "Blog Pagination")
    // Removed scrollTo to prevent jumping
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50">
      {/* Hero Section */}
      <section className="py-20 lg:py-32">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-200">
              <BookOpen className="w-3 h-3 mr-1" />
              Photography Blog
            </Badge>

            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
              Photography
              <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 bg-clip-text text-transparent">
                {" "}
                Blog
              </span>
            </h1>

            <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
              Tips, inspiration, and behind-the-scenes stories from my photography journey. Discover helpful advice for
              your sessions and get inspired by the beautiful moments we create together.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Posts Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-12">
              <h2 className="text-3xl font-bold text-gray-900">Featured Posts</h2>
              <Sparkles className="w-8 h-8 text-purple-600" />
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredPosts.map((post, index) => (
                <motion.div
                  key={post.sys.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="group h-full border-0 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden flex flex-col">
                    <div className="relative overflow-hidden">
                      <Image
                        src={post.fields.featuredImage ? `https:${post.fields.featuredImage.fields.file.url}` : "/placeholder.svg"}
                        alt={post.fields.featuredImage?.fields.title || post.fields.title}
                        width={post.fields.featuredImage?.fields.file.details?.image?.width || 800}
                        height={post.fields.featuredImage?.fields.file.details?.image?.height || 600}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <Badge className="absolute top-3 left-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                        <Sparkles className="w-3 h-3 mr-1" />
                        Featured
                      </Badge>
                    </div>

                    <CardContent className="p-4 flex flex-col flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors line-clamp-2">
                        {post.fields.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-3 line-clamp-3 flex-1">{post.fields.excerpt}</p>

                      <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                        <div className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {formatDate(post.fields.publishDate)}
                        </div>
                        <div className="flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {calculateReadTime(post.fields.content)}
                        </div>
                      </div>

                      <Button
                        asChild
                        size="sm"
                        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white mt-auto"
                      >
                        <Link href={`/blog/${post.fields.slug}`}>Continue Reading</Link>
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-12 bg-gradient-to-br from-purple-100 via-pink-100 to-rose-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
                <Search className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-3">Looking for Something Specific?</h3>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Search through all my photography tips, behind-the-scenes stories, and helpful advice
              </p>
            </div>

            <div className="relative max-w-xl mx-auto mb-6">
              <Search className="absolute left-6 top-1/2 transform -translate-y-1/2 text-gray-400 w-6 h-6" />
              <Input
                type="text"
                placeholder="Search photography tips, session advice, and more..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-16 pr-6 py-4 text-lg border-2 border-gray-200 focus:border-purple-500 focus:ring-purple-500 rounded-lg bg-white shadow-sm placeholder:text-gray-400"
              />
            </div>

            {searchQuery && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="inline-flex items-center px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full border border-white/40">
                  <Sparkles className="w-4 h-4 text-purple-600 mr-2" />
                  <span className="text-purple-800 font-medium">
                    Found {filteredPosts.length} posts matching "{searchQuery}"
                  </span>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* All Posts Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-12">
              <h2 className="text-3xl font-bold text-gray-900">{searchQuery ? "Search Results" : "All Posts"}</h2>
              <div className="text-sm text-gray-600">
                Showing {currentPosts.length} of {filteredPosts.length} posts
              </div>
            </div>

            {currentPosts.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {currentPosts.map((post, index) => (
                  <motion.div
                    key={post.sys.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="group h-full border-0 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden flex flex-col">
                      <div className="relative overflow-hidden">
                        <Image
                          src={post.fields.featuredImage ? `https:${post.fields.featuredImage.fields.file.url}` : "/placeholder.svg"}
                          alt={post.fields.featuredImage?.fields.title || post.fields.title}
                          width={post.fields.featuredImage?.fields.file.details?.image?.width || 800}
                          height={post.fields.featuredImage?.fields.file.details?.image?.height || 600}
                          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        {post.fields.featured && (
                          <Badge className="absolute top-3 left-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                            <Sparkles className="w-3 h-3 mr-1" />
                            Featured
                          </Badge>
                        )}
                      </div>

                      <CardContent className="p-4 flex flex-col flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors line-clamp-2">
                          {post.fields.title}
                        </h3>
                        <p className="text-gray-600 text-sm mb-3 line-clamp-3 flex-1">{post.fields.excerpt}</p>

                        <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                          <div className="flex items-center">
                            <Calendar className="w-3 h-3 mr-1" />
                            {formatDate(post.fields.publishDate)}
                          </div>
                          <div className="flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {calculateReadTime(post.fields.content)}
                          </div>
                        </div>

                        <Button
                          asChild
                          size="sm"
                          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white mt-auto"
                        >
                          <Link href={`/blog/${post.fields.slug}`}>Continue Reading</Link>
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No posts found</h3>
                <p className="text-gray-600 mb-6">Try adjusting your search to find what you're looking for.</p>
                <Button
                  onClick={() => setSearchQuery("")}
                  variant="outline"
                  className="border-purple-300 text-purple-700 hover:bg-purple-50 bg-transparent"
                >
                  Clear Search
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Pagination */}
      {totalPages > 1 && (
        <section className="py-8 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="border-purple-300 text-purple-700 hover:bg-purple-50 bg-transparent disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>

                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePageChange(page)}
                      className={
                        currentPage === page
                          ? "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                          : "border-purple-300 text-purple-700 hover:bg-purple-50 bg-transparent"
                      }
                    >
                      {page}
                    </Button>
                  ))}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="border-purple-300 text-purple-700 hover:bg-purple-50 bg-transparent disabled:opacity-50"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>

              <div className="text-center mt-4 text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto space-y-8 text-white">
            <Sparkles className="w-12 h-12 mx-auto" />
            <h2 className="text-3xl lg:text-4xl font-bold">Ready to Create Your Own Story?</h2>
            <p className="text-xl opacity-90">
              Let's capture beautiful moments together. Book your session and start creating memories that will last a
              lifetime.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-white text-purple-600 hover:bg-gray-100">
                <Link href="/contact" scroll={true}>
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Your Session
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 bg-transparent">
                <Link href="/contact" scroll={true}>
                  Get In Touch
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
} 